import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import cx from 'classnames';

import { ReportTypes } from 'constants/constants';

import Stats from 'store/models/Stats';
import AppState, { selectReportDateRange } from 'store/AppState';
import { selectChannels } from 'store/models/Channel';
import Brand, { selectBrands } from 'store/models/Brand';
import Campaign, { selectAllCampaigns } from 'store/models/Campaign';
import Account, { selectCurrentAccount } from 'store/models/Account';

import Modal, { ModalContent, ModalTitle, ModalSubtitle, ModalActions, ModalHeader, } from "components/Modal";
import { Button, CancelButton } from "components/Button";
import { Form, withForm } from "components/forms/Form";
import SelectInput from "components/forms/SelectInput";
import { ReportDateRange } from 'components/DateRange';

const selectors = createSelector(
  selectCurrentAccount,
  selectReportDateRange,
  selectChannels, 
  selectBrands,
  selectAllCampaigns,
  state => selectCurrentAccount(state)?.tags,
  (account, dateRange, channels, brands, campaigns, tags) => {
    channels = _.orderBy(channels, ['serviceName'], ['asc']);
    brands = _.orderBy(brands, ['name'], ['asc']);
    campaigns = _.orderBy(campaigns, ['name'], ['asc']);
    tags = _.orderBy(tags, ['name'], ['asc']);
    return {
      account,
      dateRange,
      channels,
      brands,
      campaigns, 
      tags
    }
});

const Reports = {
  [ReportTypes.ACCOUNT]: { name: "Total Combined Account", desc: "Totals across your entire account" },
  [ReportTypes.CHANNELS]: { name: "All Channels", desc: "Totals listed by your channels" },
  [ReportTypes.BRANDS]: { name: "All Brands", desc: "Totals listed by your brands" },
  [ReportTypes.STREAMS]: { name: "All Streams", desc: "Totals listed by each stream" },
  [ReportTypes.SLIDES]: { name: "All Slides", desc: "Totals listed by each slide" },
  //[ReportTypes.GAMES]: { name: "All Games", desc: "Totals listed by each game streamed" },
  [ReportTypes.TAGS]: { name: "All Channel Groups", desc: "Totals listed by your channel groups" },
  [ReportTypes.CHANNEL]: { name: "Specific Channel", desc: "Totals, links, and slide data for a specific channel" },
  [ReportTypes.BRAND]: { name: "Specific Brand", desc: "Totals, campaigns, links, and slide data for a specific brand" },
  [ReportTypes.CAMPAIGN]: { name: "Specific Campaign", desc: "Totals, campaigns, links, and slide data for a specific campaign" },
  [ReportTypes.TAG]: { name: "Specific Channel Group", desc: "Totals, campaigns, links, and slide data for a specific channel group" },
}

// ===========================
// COMPONENT
// ===========================

let ExportModal = ({ onClose, onCancel, onExport, values, errors, report, channelId, brandId, campaignId, tagId, onInputChange, setDefaults, setValues }) => {
  const dispatch = useDispatch();
  const { dateRange, channels, brands, campaigns, tags } = useSelector(selectors);
  const brandCampaigns = _.filter(campaigns, { brandId: values.brandId });

  useEffect(() => {
    dispatch(Brand.actions.fetchBrands());
    dispatch(Campaign.actions.fetchCampaigns());
    setDefaults({ 
      report: report ?? 'account', 
      channelId,
      brandId,
      campaignId,
      tagId,
    })
  }, [])

  useEffect(() => {
    setValues({
      channelId: values.channelId ?? channelId ?? _.first(_.values(channels))?._id, //fucked, chaining doesn't work here???
      brandId: values.brandId ?? brandId ?? _.first(_.values(brands))?._id,
      campaignId: values.campaignId ?? campaignId ?? _.first(_.values(brandCampaigns))?._id,
      tagId: values.tagId ?? tagId ?? _.first(tags)?.tagId,
    });
  }, [channels, brands, campaigns])

  useEffect(() => {
    setValues({
      campaignId: _.first(_.values(brandCampaigns))?._id,
    });
  }, [values.brandId])

  const onCancelClick = () => {
    onCancel?.();
    onClose?.();
  }

  const onExportClick = () => {
    switch(values.report) {
      case ReportTypes.CHANNEL: {
        dispatch(Stats.actions.exportDetailsReport(values.report, values.channelId, dateRange.dates.from, dateRange.dates.to))
        break;
      }
      case ReportTypes.BRAND: {
        dispatch(Stats.actions.exportDetailsReport(values.report, values.brandId, dateRange.dates.from, dateRange.dates.to))
        break;
      } 
      case ReportTypes.CAMPAIGN: {
        dispatch(Stats.actions.exportDetailsReport(values.report, values.campaignId, dateRange.dates.from, dateRange.dates.to))
        break;
      } 
      case ReportTypes.TAG: {
        dispatch(Stats.actions.exportDetailsReport(values.report, values.tagId, dateRange.dates.from, dateRange.dates.to))
        break;
      } 
      default: {
        dispatch(Stats.actions.exportReport(values.report, dateRange.dates.from, dateRange.dates.to))
        break;
      }
    }
    onExport?.();
    return;
  }

  const onDateChange = (r, d) => {}

  return (
    <Modal className="export" onClose={onClose}>
      <ModalHeader>
        <ModalTitle>Download Report</ModalTitle>
        <ModalSubtitle>Get excel reports of your data</ModalSubtitle>
      </ModalHeader>
      <ModalContent align="center">
        <Form onSubmit={onExportClick}>
          <div className="report-select">
            <SelectInput
              name="report"
              label="Report:"
              value={values.report}
              errors={errors.report}
              onChange={onInputChange}>
              { _.map(Reports, (v,k) => <option key={k} value={k}>{v.name}</option>)}
            </SelectInput>
            <div className="input">
              <label>Date Range:</label>
              <ReportDateRange buttonProps={{color:"modal", size: "l"}} />
            </div>
          </div>
          <div className="report-desc">
            <p>
              { Reports[values.report]?.desc }
            </p>
          </div>
          { values.report === 'channel' && 
          <div className="channel-detail-select">
            <SelectInput
              name="channelId"
              label="Channel:"
              value={values.channelId}
              errors={errors.channelId}
              onChange={onInputChange}>
              { _.map(channels, channel => <option key={channel._id} value={channel._id}>{channel.displayName ?? channel.serviceName }</option>)}
            </SelectInput>
          </div>}
          { (values.report === ReportTypes.BRAND || values.report === ReportTypes.CAMPAIGN)  && 
          <div className="channel-detail-select">
            <SelectInput
              name="brandId"
              label="Brand:"
              value={values.brandId}
              errors={errors.brandId}
              onChange={onInputChange}>
              { _.map(brands, brand => <option key={brand._id} value={brand._id}>{brand.name}</option>)}
            </SelectInput>
          </div>}
          { values.report === ReportTypes.CAMPAIGN && values.brandId && 
          <div className="channel-detail-select">
            <SelectInput
              name="campaignId"
              label="Campaign:"
              value={values.campaignId}
              errors={errors.campaignId}
              onChange={onInputChange}>
              { _.map(brandCampaigns, campaign => <option key={campaign._id} value={campaign._id}>{campaign.name}</option>)}
            </SelectInput>
          </div>}
          { values.report === ReportTypes.TAG && values.tagId && 
          <div className="channel-detail-select">
            <SelectInput
              name="tagId"
              label="Channel Group:"
              value={values.tagId}
              errors={errors.tagId}
              onChange={onInputChange}>
              { _.map(tags, tag => <option key={tag.tagId} value={tag.tagId}>{tag.name}</option>)}
            </SelectInput>
          </div>}
        </Form>
      </ModalContent>
      <ModalActions dir="row">
        <CancelButton size="l" onClick={onCancelClick}>Cancel</CancelButton>
        <Button size="l" onClick={onExportClick}>Download</Button>
      </ModalActions>
    </Modal>);
}

export default withForm(ExportModal);
